import { useSpeakerHeadshot } from '@src/hooks/useSpeakerHeadshot';
import { useDatoClient } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getImpactScore } from '@src/utility/impactScore';
import { memo, useMemo } from 'react';
import styled from 'styled-components';

import { getPosterUrl } from '../../../utility/general';
import { formatStoryteller } from '../../../utility/shareableImages';
import ImpactScoreBadge from '../../sidepanel/ImpactScoreBadge';
import { usePreviewSupersample } from './usePreviewSupersample';
import { useQuoteCardRenderer } from './useQuoteCardRenderer';

import type { ShareableImageType } from '../../../types.ts/story';
import type { CompositionTemplate } from '../../sidepanel/CompositionSettings/types';

type Props = {
  quote: ShareableImageType;
  isSelected: boolean;
  onSelect: (id: string) => void;
  compositionTemplates: CompositionTemplate[];
};

// The header row is a fixed height that fits the score badge, so a card does not
// grow when its score arrives after its text, and the results modal's skeleton
// card uses the same value so nothing shifts when a real card replaces it.
export const CARD_HEADER_ROW_HEIGHT = '36px';

const PullQuoteVisualCard = memo(
  ({ quote, isSelected, onSelect, compositionTemplates }: Props) => {
    const videoCreator = useVideoCreatorStore();
    const datoClientStore = useDatoClient();
    const defaultPullQuoteId =
      videoCreator.organization?.defaultPullQuoteTemplate?.id;

    const compositionTemplate = useMemo(
      () =>
        compositionTemplates.find(t => t.id === quote.parentComposition?.id) ||
        (defaultPullQuoteId
          ? compositionTemplates.find(t => t.id === defaultPullQuoteId)
          : undefined) ||
        compositionTemplates.find(t => !t.isGlobalPreset) ||
        compositionTemplates.find(
          t => t.isGlobalPreset && t.internalName.includes('Default'),
        ) ||
        compositionTemplates.find(t => t.isGlobalPreset) ||
        compositionTemplates[0],
      [compositionTemplates, quote.parentComposition?.id, defaultPullQuoteId],
    );

    const impactScore = getImpactScore(quote.metadata);

    const background = getPosterUrl(
      quote.imagefile?.url || '',
      1080,
      1080,
      false,
    );
    const storyteller = quote.storytellerName || '';
    const { name, org } = formatStoryteller(storyteller);

    const speakerHeadshotUrl = useSpeakerHeadshot({
      speakerId: quote.speaker?.id,
      speakerName: name,
      storyId: videoCreator.story?.id,
      datoClientStore,
    });

    const { rendererContainerRef } = useQuoteCardRenderer({
      text: quote.quote,
      background,
      name,
      org,
      headshot: speakerHeadshotUrl,
      compositionTemplate: compositionTemplate!,
      quoteCardSource: quote.source || {},
    });

    const { footprintRef, width, height, scale } = usePreviewSupersample();

    // Don't render if no composition template available
    if (!compositionTemplate) {
      return null;
    }

    return (
      <Card onClick={() => onSelect(quote.id)} $isSelected={isSelected}>
        <CardHeader>
          {impactScore != null && Object.keys(impactScore).length > 0 && (
            <ImpactScoreBadge
              impactScore={impactScore}
              id={`quote-visual-${quote.id}`}
            />
          )}
          <SelectButton active={isSelected}>
            {isSelected ? '✓' : ''}
          </SelectButton>
        </CardHeader>
        <RendererWrapper ref={footprintRef}>
          <RendererContainer
            ref={rendererContainerRef}
            style={{
              width,
              height,
              transform: `scale(${scale})`,
            }}
          />
        </RendererWrapper>
      </Card>
    );
  },
);

PullQuoteVisualCard.displayName = 'PullQuoteVisualCard';

export default PullQuoteVisualCard;

const Card = styled.div<{ $isSelected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background-color: var(--surface);
  box-sizing: border-box;
  cursor: pointer;
  height: fit-content;
  transition:
    box-shadow var(--transition-normal) ease,
    border-color var(--transition-fast) ease;
  border: 1.5px solid
    ${props => (props.$isSelected ? 'var(--primary)' : 'var(--border)')};
  overflow: hidden;

  &:hover {
    box-shadow: var(--elevation-sm);
    border-color: ${props =>
      props.$isSelected ? 'var(--primary)' : 'var(--text-muted)'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 0;
  min-height: ${CARD_HEADER_ROW_HEIGHT};
`;

const SelectButton = styled.div.attrs((props: { active: boolean }) => props)`
  cursor: pointer;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-left: auto;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 1.5px solid var(--text-muted);
  color: var(--text-muted);
  transition: all var(--transition-fast) ease;
  flex-shrink: 0;
  ${props =>
    props.active &&
    `
      background-color: var(--primary);
      border-color: var(--primary);
      color: var(--background);
    `}
`;

const RendererWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
`;

// Sized/scaled inline via usePreviewSupersample so the iframe rasterizes at a
// higher resolution than the card footprint, then CSS-downscales to fit.
const RendererContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  overflow: hidden;
`;
